import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { staffFeedApi } from "@/components/app/community/staff-feed-api";
import { CommunityRail } from "@/routes/_authenticated/community/-components/community-rail";
import { FeedTab } from "@/routes/_authenticated/community/-components/feed-tab";
import { useCommunityBuilding } from "@/routes/_authenticated/community/-components/use-community-building";
import { POST_CATEGORIES, type PostCategory } from "@alm/domain";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { usePolicy } from "@/lib/policy";

export const Route = createFileRoute("/_authenticated/community/")({
  validateSearch: (search: Record<string, unknown>): { category?: PostCategory } => ({
    category: optionalOneOf(POST_CATEGORIES, search.category),
  }),
  component: CommunityFeedPage,
});

/** The building's family community feed, as staff run it. */
function CommunityFeedPage() {
  const { me } = usePolicy();
  const category = Route.useSearch().category ?? "";
  const navigate = Route.useNavigate();
  const { building } = useCommunityBuilding();
  const facilityId = building?.id ?? null;
  const api = useMemo(() => staffFeedApi(facilityId, me.buildings), [facilityId, me.buildings]);
  const settings = useQuery({
    ...orpc.community.feed.settings.get.queryOptions({ input: { facilityId: facilityId ?? "" } }),
    enabled: facilityId !== null,
    retry: false,
  });
  if (!facilityId) return null;
  return (
    <>
      <PageHeader title="Community" />
      <div className="@container/feed">
        <div className="flex items-start gap-6">
          <div className="w-full max-w-150 min-w-0">
            <FeedTab
              facilityId={facilityId}
              api={api}
              category={category}
              onCategory={(next) =>
                void navigate({
                  search: { category: next || undefined },
                  ...KEEP_PLACE,
                })
              }
              commentsOn={settings.data?.commentsEnabled ?? true}
            />
          </div>
          <CommunityRail facilityId={facilityId} />
        </div>
      </div>
    </>
  );
}
